import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const GA_MEASUREMENT_ID = 'G-GZRCBYZCLH';

/**
 * Pushes a GA4 page_view on every route change and enriches it with:
 *   - `language`: current UI language (de / en)
 *   - `content_group`: the top-level section ('course', 'case-study', 'faq', …)
 *
 * GA4 treats `language` as a user_property so it's available as a dimension
 * in every report. Also sets it as a page-level custom dim for flexibility.
 *
 * Requires the base `gtag('config', '<ID>')` call already in index.html.
 */
export function GA4Tracker() {
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    // BrowserRouter has basename = /{lang}, so we rebuild the full URL Google
    // sees with language prefix.
    const path = `/${language}${location.pathname === '/' ? '' : location.pathname}${location.search}`;
    const firstSegment = location.pathname.split('/').filter(Boolean)[0] || 'home';

    // 1. Persistent user property — surfaces as a dimension in all reports
    window.gtag('set', 'user_properties', { language });

    // 2. Page-level dimension + explicit page_view with custom params
    window.gtag('event', 'page_view', {
      page_location: window.location.origin + path,
      page_path: path,
      page_title: document.title,
      language,
      content_group: firstSegment,
      send_to: GA_MEASUREMENT_ID
    });
  }, [location.pathname, location.search, language]);

  return null;
}
